"use client";

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface CvBuilderUpgradeState {
  hasUpgraded: boolean;
  dismissedThisSession: boolean;
  snoozedUntil: number | null;
  hasHydrated: boolean;

  shouldShowModal: () => boolean;
  /** Backdrop, Escape, or the close button: quiet for a few minutes. */
  snooze: () => void;
  /** The user explicitly chose the free download: quiet for the session. */
  dismiss: () => void;
  markUpgraded: () => void;
  reset: () => void;
}

/**
 * An incidental close is not a decision. Suppressing for the whole session on
 * any dismissal meant one stray Escape unlocked every later download, which is
 * how 39 users produced 83 downloads against 45 modal impressions.
 */
const SNOOZE_MINUTES = 5;

// Surfacing mirrors the CV review upgrade prompt (see upgrade-prompt-store.ts):
// dismissals are scoped to the browser session so the prompt reliably resurfaces
// in a new session or when storage is cleared, and it is not permanently
// suppressed after N dismissals. Upgrades persist across sessions in
// localStorage; the session dismissal flag lives in sessionStorage.
const SESSION_DISMISS_KEY = "cv-builder-upgrade-dismissed";

function readSessionDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSessionDismissed(value: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      window.sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } else {
      window.sessionStorage.removeItem(SESSION_DISMISS_KEY);
    }
  } catch {
    // sessionStorage can throw in private mode; treat as not-dismissed.
  }
}

let storage: ReturnType<typeof createJSONStorage> | undefined;

if (typeof window !== "undefined") {
  storage = createJSONStorage(() => localStorage);
}

const cvBuilderUpgradeStore = create<CvBuilderUpgradeState>()(
  persist(
    (set, get) => ({
      hasUpgraded: false,
      dismissedThisSession: readSessionDismissed(),
      snoozedUntil: null,
      hasHydrated: false,

      shouldShowModal: () => {
        const state = get();

        // Never show once the user has upgraded (persists across sessions).
        if (state.hasUpgraded) {
          return false;
        }

        // Hidden for the rest of this session after an explicit free download.
        if (state.dismissedThisSession) {
          return false;
        }

        // Briefly quiet after an incidental close.
        if (state.snoozedUntil !== null && Date.now() < state.snoozedUntil) {
          return false;
        }

        return true;
      },

      snooze: () => {
        set({ snoozedUntil: Date.now() + SNOOZE_MINUTES * 60 * 1000 });
      },

      dismiss: () => {
        writeSessionDismissed(true);
        set({ dismissedThisSession: true });
      },

      markUpgraded: () => {
        set({ hasUpgraded: true });
      },

      reset: () => {
        writeSessionDismissed(false);
        set({
          dismissedThisSession: false,
          snoozedUntil: null,
          hasUpgraded: false,
        });
      },
    }),
    {
      name: "cv-builder-upgrade-storage",
      storage,
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hasHydrated = true;
          // Session dismissal lives in sessionStorage, not the persisted blob,
          // so re-sync it after the localStorage rehydrate.
          state.dismissedThisSession = readSessionDismissed();
        }
      },
      partialize: (state) => ({
        hasUpgraded: state.hasUpgraded,
      }),
    },
  ),
);

export const useCvBuilderUpgradeStore = cvBuilderUpgradeStore;
